import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import SolarFieldCanvas from "./SolarFieldCanvas";
import ContactUs from "../contacts/ContactUs";
import WaveDivider from "../../ui/WaveDivider";

/* -------------------------------- Main --------------------------------- */
function Main() {
  return (
    <div className="font-inter text-text-body">
      <main className="space-y-0">
        {/* HERO — live WebGL solar field */}
        <Hero />

        {/* SOCIAL PROOF STRIP */}
        <SocialProofStrip />

        {/* ABOUT */}
        <AboutSimple />

        {/* about → calculator — warm analogous "daylight" gradient (amber → gold) */}
        <WaveDivider
          topColor="#FAFAF8"
          bottomColor="#F4F1EC"
          gradient={["#FCE6B0", "#EAC074", "#DDB05E"]}
          height={130}
        />

        {/* SOLAR CALCULATOR */}
        <SolarCalculator />

        {/* calculator → contacts — "sunset": warm sun light sinking into night */}
        <WaveDivider
          topColor="#F4F1EC"
          bottomColor="#1A1A1A"
          gradient={["#F6BE4D", "#D17A12", "#241408"]}
          height={150}
        />

        {/* CONTACTS (kept on landing) */}
        <ContactUs />
      </main>
    </div>
  );
}

/* --------------------------------- Hero -------------------------------- */
function Hero() {
  return (
    <section
      id="home"
      className="relative isolate flex min-h-[92vh] flex-col overflow-hidden pt-16 sm:pt-20 scroll-mt-16"
    >
      {/* Sky gradient — also the graceful fallback if WebGL is unavailable */}
      <div className="absolute inset-0 -z-20 bg-gradient-to-b from-[#EAF3FB] via-[#FBF3E2] to-[#F6EEDD]" />

      {/* Live 3D solar field */}
      <SolarFieldCanvas className="absolute inset-0 -z-10" />

      {/* Layered light scrim — keeps the upper text zone bright so dark copy stays crisp */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[64%] -z-10 bg-gradient-to-b from-white/88 via-white/45 to-transparent" />
      <div
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 60% 42% at 50% 24%, rgba(255,255,255,0.78), transparent 70%)",
        }}
      />

      {/* Bottom fade into the page */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 -z-10 bg-gradient-to-b from-transparent to-bg-base" />

      {/* Content */}
      <div className="relative z-10 flex flex-1 items-start justify-center">
        <div className="mx-auto w-full max-w-5xl px-5 sm:px-8 md:px-10 pt-[7vh] md:pt-[9vh] pb-28 sm:pb-24 text-center">
          <motion.div
            className="inline-flex items-center gap-2.5 rounded-full px-4 py-1.5 ring-1 ring-accent-green/25 bg-white/60 backdrop-blur-sm shadow-sm"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-green opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-green" />
            </span>
            <span className="uppercase tracking-[0.18em] text-[11px] text-accent-green font-semibold">
              Clean Energy for Filipino Homes
            </span>
          </motion.div>

          <motion.h1
            className="mt-6 font-display text-[2.75rem] sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#0F2A4A] leading-[1.05] tracking-tight [text-shadow:0_1px_12px_rgba(255,255,255,0.75)]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Stop Paying More.<br />
            <span className="bg-gradient-to-r from-amber-600 via-orange-600 to-amber-600 bg-clip-text text-transparent [text-shadow:none]">
              Start Owning Your Power.
            </span>
          </motion.h1>

          <motion.p
            className="mt-6 text-lg md:text-xl text-slate-700 font-medium max-w-2xl mx-auto leading-relaxed [text-shadow:0_1px_8px_rgba(255,255,255,0.9)]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.32, duration: 0.8 }}
          >
            Solar systems engineered for Filipino homes — grid-tie, hybrid,
            professionally installed and fully warranted.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.44, duration: 0.8 }}
            className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <a
              href="#calculator"
              className="group btn-solar w-full sm:w-auto px-7 py-3.5 text-[15px]"
            >
              <span>Compute My Savings</span>
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </a>
            <Link
              to="/products"
              className="btn-ghost w-full sm:w-auto px-7 py-3.5 text-[15px]"
            >
              <span>Browse Systems</span>
            </Link>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="mt-12 inline-flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm rounded-2xl bg-white/80 backdrop-blur-md border border-white/80 px-6 py-3.5 shadow-md ring-1 ring-black/5"
          >
            <div className="flex items-center gap-2">
              <span className="text-base">🛡️</span>
              <span className="text-text-body font-medium">12-Year Warranty</span>
            </div>
            <div className="hidden sm:block h-4 w-px bg-border-light self-center" />
            <div className="flex items-center gap-2">
              <span className="text-base">⚡</span>
              <span className="text-text-body font-medium">DEYE & SOLIS Inverters</span>
            </div>
            <div className="hidden sm:block h-4 w-px bg-border-light self-center" />
            <div className="flex items-center gap-2">
              <span className="text-base">🔋</span>
              <span className="text-text-body font-medium">LiFePO₄ Battery</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.a
        href="#about"
        aria-label="Scroll to learn more"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-1 text-text-muted hover:text-text-primary transition-colors"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.8 }}
      >
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold">Scroll</span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="text-lg leading-none"
        >
          ↓
        </motion.span>
      </motion.a>
    </section>
  );
}

/* ---------------------- ABOUT: SIMPLE FULL-BLEED ----------------------- */
function AboutSimple() {
  return (
    <section id="about" className="scroll-mt-16">
      <motion.div
        className="relative w-full"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true, margin: "-20% 0px -20% 0px" }}
      >
        {/* full-width section background */}
        <div className="w-full bg-base text-text-body">
          <div className="py-20 md:py-28">
            {/* readable content width inside the full-bleed band */}
            <div className="mx-auto max-w-[92rem] px-5 sm:px-8 md:px-10">
              <SectionHeaderSimple eyebrow="How Solar Works" title="Sunlight to Savings" />
              <p className="mt-6 text-lg text-text-body text-center max-w-3xl mx-auto leading-relaxed">
                Solar converts sunlight into electricity through PV modules
                (DC), which an inverter turns into AC for your home or business.
                With net metering, excess daytime energy earns credits;
                optional batteries store power for night and outages.
              </p>

              <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
                <SimpleCard icon="💰" title="Lower Operating Costs">
                  Generate your own electricity and hedge against rate hikes.
                  Credits can offset your bill over the year.
                </SimpleCard>
                <SimpleCard icon="⚡" title="Reliable & Scalable">
                  Start small and expand. Add batteries for backup or
                  peak-shaving when it makes sense.
                </SimpleCard>
                <SimpleCard icon="🌱" title="Clean, Quiet Power">
                  No fuel, no noise, minimal maintenance — cut emissions while
                  improving onsite resilience.
                </SimpleCard>
              </div>

              <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
                <div className="space-y-6">
                  <div className="inline-flex items-center gap-2">
                    <span className="h-px w-8 bg-accent-sun" />
                    <span className="uppercase tracking-[0.18em] text-[11px] text-accent-sun font-semibold">
                      Who We Are
                    </span>
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl font-bold text-text-primary tracking-tight leading-tight">
                    Engineering-first solar for{" "}
                    <span className="text-accent-sun">Filipino homes</span>
                  </h3>
                  <p className="text-text-body leading-relaxed text-[15px]">
                    <span className="font-semibold text-text-primary">SOLARES</span> designs
                    and builds high-efficiency PV systems — tidy installs, clear ROI
                    guidance, and engineering-first layouts.
                  </p>
                  <ul className="space-y-4 text-[15px] leading-relaxed text-text-body border-l-2 border-accent-sun/30 pl-5">
                    <li>
                      <span className="font-semibold text-text-primary">Services — </span>
                      survey & modeling, sizing, drawings, permitting, installation, monitoring, after-sales.
                    </li>
                    <li>
                      <span className="font-semibold text-text-primary">Approach — </span>
                      transparent proposals, strong warranties, clean cable management, inspector-ready docs.
                    </li>
                    <li>
                      <span className="font-semibold text-text-primary">Safety — </span>
                      proper conductor sizing, SPD/MCB/RCD, grounding/bonding, and clear labels.
                    </li>
                  </ul>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <KPI value="98%" label="On-time Installs" />
                  <KPI value="25–30 yr" label="Panel lifespan" />
                  <KPI value="24/7" label="Monitoring" />
                  <div className="col-span-3 p-7 rounded-2xl bg-gradient-to-br from-accent-sun/10 to-amber-50 border border-accent-sun/20">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-lg">📋</span>
                      <h4 className="font-display font-bold text-text-primary tracking-tight">
                        Typical Deliverables
                      </h4>
                    </div>
                    <ul className="text-sm space-y-2.5 text-text-body">
                      <li className="flex gap-2">
                        <span className="text-accent-sun mt-0.5">✓</span>
                        <span>One-line & layout drawings</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-accent-sun mt-0.5">✓</span>
                        <span>String/MPPT configuration & voltage checks</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-accent-sun mt-0.5">✓</span>
                        <span>Bill of materials & warranties</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-accent-sun mt-0.5">✓</span>
                        <span>Owner's manual & maintenance checklist</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            {/* /max-w for readability only */}
          </div>
        </div>
      </motion.div>
    </section>
  );
}

/* ----------------------- Presentational helpers ------------------------ */
function SectionHeaderSimple({ title, eyebrow }) {
  return (
    <div className="text-center">
      {eyebrow && (
        <div className="inline-flex items-center gap-2 mb-4">
          <span className="h-px w-8 bg-accent-sun" />
          <span className="uppercase tracking-[0.18em] text-[11px] text-accent-sun font-semibold">
            {eyebrow}
          </span>
          <span className="h-px w-8 bg-accent-sun" />
        </div>
      )}
      <h2 className="font-display text-3xl md:text-5xl font-bold text-text-primary tracking-tight leading-tight">
        {title}
      </h2>
    </div>
  );
}

function KPI({ value, label }) {
  return (
    <div className="p-5 text-center rounded-xl bg-card border border-border-light hover:border-accent-sky hover:shadow-lg hover:-translate-y-0.5 transition-all">
      <div className="font-numeric text-2xl md:text-3xl font-bold text-accent-sky">{value}</div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.15em] text-text-muted font-semibold">
        {label}
      </div>
    </div>
  );
}

function SimpleCard({ icon, title, children }) {
  return (
    <div className="group p-7 rounded-2xl bg-card border border-border-light hover:border-accent-sun/40 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-accent-sun/10 text-2xl mb-4 group-hover:bg-accent-sun/20 group-hover:scale-110 transition-all">
        {icon}
      </div>
      <h3 className="font-display font-semibold text-lg text-text-primary tracking-tight">{title}</h3>
      <p className="mt-2 text-sm text-text-body leading-relaxed">{children}</p>
    </div>
  );
}

/* ------------------- VALUE STRIP (ICON GRID) ------------------- */
const VALUE_PROPS = [
  { icon: "🛡️", label: "12-Year Warranty" },
  { icon: "⚡", label: "DEYE & SOLIS" },
  { icon: "🔋", label: "LiFePO₄ Battery" },
  { icon: "📋", label: "Full Permits" },
];

function SocialProofStrip() {
  return (
    <div className="relative w-full bg-gradient-to-b from-white to-bg-base border-y border-border-light py-10">
      <div className="mx-auto max-w-[92rem] px-5 sm:px-8 md:px-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:divide-x md:divide-border-light">
          {VALUE_PROPS.map((item) => (
            <div
              key={item.label}
              className="group flex items-center justify-center gap-3 md:px-4"
            >
              <span className="text-3xl transition-transform duration-300 group-hover:scale-110">
                {item.icon}
              </span>
              <span className="text-sm font-semibold text-text-primary tracking-tight transition-colors group-hover:text-accent-sun">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------- SOLAR CALCULATOR (NEW) ------------------- */
/* Promo catalog mirrors the Products page. gen ≈ kW × 130 kWh/month. */
const GEN_PER_KW = 130;
const SYSTEM_TYPES = [
  {
    id: "grid", label: "Grid-Tie", battery: null, offset: 0.65, storageKwh: 0,
    sizes: [
      { kw: 3.72, price: 180000, srp: 200000 },
      { kw: 5.58, price: 220000, srp: 240000 },
      { kw: 6.82, price: 238000, srp: 258000 },
      { kw: 8.68, price: 289000, srp: 309000 },
      { kw: 10.54, price: 330000, srp: 350000 },
    ],
  },
  {
    id: "h100", label: "Hybrid", battery: "100Ah", batteryAh: 100, offset: 0.75, storageKwh: 4.6,
    sizes: [
      { kw: 3.72, price: 260000, srp: 280000 },
      { kw: 5.58, price: 285000, srp: 305000 },
      { kw: 6.82, price: 313000, srp: 333000 },
      { kw: 8.68, price: 363000, srp: 383000 },
      { kw: 10.54, price: 422000, srp: 442000 },
      { kw: 12.4, price: 470000, srp: 495000 },
    ],
  },
  {
    id: "h200", label: "Hybrid", battery: "200Ah", batteryAh: 200, offset: 0.8, storageKwh: 9.2,
    sizes: [
      { kw: 5.58, price: 335000, srp: 355000 },
      { kw: 6.82, price: 360000, srp: 380000 },
      { kw: 8.68, price: 410000, srp: 430000 },
      { kw: 10.54, price: 470000, srp: 495000 },
      { kw: 12.4, price: 516000, srp: 541000 },
      { kw: 16.7, price: 607000, srp: 647000 },
    ],
  },
  {
    id: "h314", label: "Hybrid", battery: "314Ah", batteryAh: 314, offset: 0.85, storageKwh: 14.5,
    sizes: [
      { kw: 6.82, price: 385000, srp: 405000 },
      { kw: 8.68, price: 438000, srp: 458000 },
      { kw: 10.54, price: 490000, srp: 510000 },
      { kw: 12.4, price: 534000, srp: 564000 },
      { kw: 16.7, price: 630000, srp: 680000 },
      { kw: 18.6, price: 724000, srp: 774000 },
    ],
  },
];

// Sizing rule: ~1 kW per ₱1,000 of monthly bill, snapped to the nearest
// available system size within the chosen type.
function recommendSystem(typeId, targetKW) {
  const type = SYSTEM_TYPES.find((t) => t.id === typeId) || SYSTEM_TYPES[0];
  const match = type.sizes.reduce(
    (best, s) =>
      Math.abs(s.kw - targetKW) < Math.abs(best.kw - targetKW) ? s : best,
    type.sizes[0]
  );
  return {
    ...match,
    label: type.label,
    battery: type.battery,
    batteryAh: type.batteryAh,
    offset: type.offset,
    storageKwh: type.storageKwh,
    tag: `${match.kw} kW ${type.label}`,
  };
}

function SolarCalculator() {
  const [inputType, setInputType] = React.useState("bill"); // 'bill' or 'kwh'
  const [systemType, setSystemType] = React.useState("grid"); // grid | h100 | h200 | h314
  const [billValue, setBillValue] = React.useState(5000);
  const [kwhValue, setKwhValue] = React.useState(450);
  const [meralcoRate, setMeralcoRate] = React.useState(11);

  const kwhPerMonth =
    inputType === "bill" ? Math.round(billValue / meralcoRate) : kwhValue;
  const monthlyBill =
    inputType === "bill" ? billValue : Math.round(kwhValue * meralcoRate);

  // ~1 kW per ₱1,000 of monthly bill
  const targetKW = monthlyBill / 1000;
  const recommended = recommendSystem(systemType, targetKW);
  const monthlyGeneration = recommended.kw * GEN_PER_KW;
  // Realistic bill offset, scaled by battery size (more storage = more night
  // self-consumption). Grid-tie has no battery and exports surplus cheaply.
  const offsetFactor = recommended.offset;
  const energyOffset = Math.min(monthlyGeneration, kwhPerMonth);
  const monthlySavings = energyOffset * meralcoRate * offsetFactor;

  // Battery (capacity method, per SolarPH): nameplate kWh × rate = max saved per
  // full charge–discharge cycle; ×~30 ≈ monthly ceiling if cycled daily.
  const BATTERY_VOLTAGE = 51.2;
  const batteryCapacityKwh = recommended.batteryAh
    ? (recommended.batteryAh * BATTERY_VOLTAGE) / 1000
    : 0;
  const batterySavingsPerCycle = batteryCapacityKwh * meralcoRate;
  const batteryMonthlyMax = batterySavingsPerCycle * 30;
  const backupHours = Math.round(recommended.storageKwh); // ≈ hours at ~1 kW essential load
  const paybackYears = (recommended.price / (monthlySavings * 12)).toFixed(1);
  const netSavings25yr = Math.round(monthlySavings * 12 * 25 - recommended.price);

  return (
    <section id="calculator" className="scroll-mt-16">
      <motion.div
        className="relative w-full"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true, margin: "-20% 0px -20% 0px" }}
      >
        <div className="w-full bg-section py-20 md:py-28">
          <div className="mx-auto max-w-[92rem] px-5 sm:px-8 md:px-10">
            <SectionHeaderSimple eyebrow="Savings Calculator" title="How Much Can You Save?" />
            <p className="mt-6 text-lg text-text-body text-center mb-14 max-w-2xl mx-auto leading-relaxed">
              Enter your monthly bill or average kWh usage to see your potential savings.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
              {/* Input Panel */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                viewport={{ once: true }}
                className="flex h-full flex-col rounded-2xl border border-border-light bg-card p-6 md:p-8"
              >
                <div className="space-y-6">
                {/* Segmented toggle */}
                <div className="grid grid-cols-2 gap-1 rounded-xl bg-bg-section p-1 ring-1 ring-border-light">
                  <button
                    onClick={() => setInputType("bill")}
                    className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
                      inputType === "bill"
                        ? "bg-card text-accent-sun shadow-sm ring-1 ring-black/5"
                        : "text-text-muted hover:text-text-primary"
                    }`}
                  >
                    Monthly Bill (₱)
                  </button>
                  <button
                    onClick={() => setInputType("kwh")}
                    className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
                      inputType === "kwh"
                        ? "bg-card text-accent-sun shadow-sm ring-1 ring-black/5"
                        : "text-text-muted hover:text-text-primary"
                    }`}
                  >
                    Monthly kWh
                  </button>
                </div>

                {/* System Type selector */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-text-body">System Type</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {SYSTEM_TYPES.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setSystemType(t.id)}
                        className={`px-3 py-2 rounded-lg text-sm transition-all border ${
                          systemType === t.id
                            ? "border-accent-sun bg-accent-sun/10 text-accent-sun font-semibold ring-1 ring-accent-sun/30 shadow-sm"
                            : "bg-card text-text-body border-border-light font-medium hover:border-accent-sun/60 hover:text-text-primary"
                        }`}
                      >
                        {t.battery ? `Hybrid ${t.battery}` : "Grid-Tie"}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-text-muted">
                    {systemType === "grid"
                      ? "Lowest upfront cost — offsets daytime usage, no battery backup."
                      : "Adds LiFePO₄ battery backup for night use and brownouts."}
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-text-muted">
                      {inputType === "bill" ? "Monthly Bill" : "Monthly kWh"}
                    </label>
                    <span className="text-2xl font-bold text-accent-sky">
                      {inputType === "bill" ? `₱${billValue.toLocaleString()}` : `${kwhValue} kWh`}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={inputType === "bill" ? "500" : "50"}
                    max={inputType === "bill" ? "20000" : "1800"}
                    step={inputType === "bill" ? "100" : "10"}
                    value={inputType === "bill" ? billValue : kwhValue}
                    onChange={(e) => {
                      if (inputType === "bill") setBillValue(parseInt(e.target.value));
                      else setKwhValue(parseInt(e.target.value));
                    }}
                    className="w-full h-2 bg-border-light rounded-lg appearance-none cursor-pointer accent-accent-sun"
                  />
                  <div className="flex justify-between text-xs text-text-muted">
                    <span>{inputType === "bill" ? "₱500" : "50 kWh"}</span>
                    <span>{inputType === "bill" ? "₱20,000" : "1,800 kWh"}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-text-body">
                    Meralco Rate: ₱{meralcoRate}/kWh
                  </label>
                  <input
                    type="number"
                    min="8"
                    max="15"
                    step="0.5"
                    value={meralcoRate}
                    onChange={(e) => setMeralcoRate(parseFloat(e.target.value))}
                    className="w-full px-4 py-2.5 rounded-lg bg-card border border-border-light text-text-primary font-mono focus:outline-none focus:ring-2 focus:ring-accent-sun"
                  />
                </div>
                </div>

                <div className="mt-auto pt-6">
                  <button
                    onClick={() => {}}
                    className="btn-solar w-full px-6 py-3.5 hidden md:inline-flex"
                  >
                    Compute Savings
                  </button>
                </div>
              </motion.div>

              {/* Results Panel */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                viewport={{ once: true }}
                className="flex h-full flex-col rounded-2xl border border-border-light bg-card p-6 md:p-8"
              >
                <div className="space-y-6">
                {/* Recommended System Card */}
                <div className="p-6 rounded-xl bg-accent-sun-light/30 border-2 border-accent-sun shadow-md">
                  <div className="text-sm font-medium text-accent-sun uppercase tracking-widest mb-2">
                    Recommended System
                  </div>
                  <div className="flex items-center flex-wrap gap-2 mb-1">
                    <div className="text-3xl md:text-4xl font-bold text-text-primary">
                      {recommended.tag}
                    </div>
                    {recommended.battery && (
                      <span className="rounded-full bg-accent-green/15 border border-accent-green/30 px-2.5 py-0.5 text-xs font-bold text-accent-green">
                        🔋 {recommended.battery}
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-2xl text-accent-sun font-sharetech font-bold">
                      ₱{recommended.price.toLocaleString()}
                    </span>
                    <span className="text-sm text-text-muted line-through">
                      ₱{recommended.srp.toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] font-bold uppercase tracking-wide text-accent-green">
                    ☀️ Summer Promo price
                  </div>
                  <div className="mt-3 pt-3 border-t border-accent-sun/20 text-xs text-text-body">
                    ≈1&nbsp;kW per ₱1,000 bill. Generates ~{Math.round(monthlyGeneration).toLocaleString()} kWh/mo
                    {" "}— covers your ~{kwhPerMonth.toLocaleString()} kWh usage with room to grow.
                  </div>
                </div>

                {/* Savings Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-section border border-border-light">
                    <div className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1">
                      Monthly Savings
                    </div>
                    <div className="text-2xl font-bold text-accent-sun">
                      ₱{monthlySavings.toLocaleString("en-PH", {
                        maximumFractionDigits: 0,
                      })}
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-section border border-border-light">
                    <div className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1">
                      Payback Period
                    </div>
                    <div className="text-2xl font-bold text-accent-sky">
                      {paybackYears} yrs
                    </div>
                  </div>
                </div>

                {/* 25-Year Savings */}
                <div className="p-4 rounded-lg bg-accent-green/10 border border-accent-green/30">
                  <div className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1">
                    25-Year Net Savings
                  </div>
                  <div className="text-3xl font-bold text-accent-green">
                    ₱{netSavings25yr.toLocaleString()}
                  </div>
                </div>

                {/* Battery savings & backup (hybrid only) */}
                {recommended.battery && (
                  <div className="p-4 rounded-lg bg-section border border-border-light">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-base">🔋</span>
                      <span className="text-xs font-bold uppercase tracking-wide text-text-primary">
                        Battery Savings &amp; Backup · {recommended.battery}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-[11px] text-text-muted uppercase tracking-wide mb-0.5">
                          Per full cycle
                        </div>
                        <div className="text-xl font-bold text-accent-green">
                          ₱{batterySavingsPerCycle.toLocaleString("en-PH", { maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] text-text-muted uppercase tracking-wide mb-0.5">
                          Up to / month
                        </div>
                        <div className="text-xl font-bold text-accent-green">
                          ₱{Math.round(batteryMonthlyMax).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <p className="mt-3 text-xs text-text-body">
                      {recommended.batteryAh}Ah × {BATTERY_VOLTAGE}V = {batteryCapacityKwh.toFixed(2)} kWh stored.
                      Used at night vs ₱{meralcoRate}/kWh grid, that's up to{" "}
                      ₱{batterySavingsPerCycle.toLocaleString("en-PH", { maximumFractionDigits: 2 })} per full
                      charge–discharge (~₱{Math.round(batteryMonthlyMax).toLocaleString()}/mo if cycled daily).
                      Also powers essentials ~{backupHours} hrs during brownouts.
                    </p>
                  </div>
                )}

                </div>

                {/* CTAs */}
                <div className="mt-auto pt-6 space-y-3">
                  <Link
                    to="/products"
                    className="btn-ghost w-full px-6 py-3"
                  >
                    → View This Package
                  </Link>
                  <button
                    onClick={() => document.querySelector("#contacts")?.scrollIntoView({ behavior: "smooth" })}
                    className="btn-solar w-full px-6 py-3"
                  >
                    → Request a Quote
                  </button>
                </div>
              </motion.div>
            </div>

            {/* How the estimate is calculated */}
            <motion.div
              className="mt-12 max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="rounded-2xl border border-border-light bg-card p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-text-primary mb-1">
                  How your estimate is calculated
                </h3>
                <p className="text-sm text-text-muted mb-6">
                  Every figure updates live from your inputs — here's the step-by-step.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <CalcStep
                    n="1"
                    label="Your usage"
                    value={`~${kwhPerMonth.toLocaleString()} kWh/mo`}
                    note={
                      inputType === "bill"
                        ? `₱${billValue.toLocaleString()} ÷ ₱${meralcoRate}/kWh`
                        : "entered directly"
                    }
                  />
                  <CalcStep
                    n="2"
                    label="System output"
                    value={`~${Math.round(monthlyGeneration).toLocaleString()} kWh/mo`}
                    note={`${recommended.kw} kW × ~${GEN_PER_KW} kWh/kW`}
                  />
                  <CalcStep
                    n="3"
                    label="Energy offset"
                    value={`~${Math.round(Math.min(monthlyGeneration, kwhPerMonth)).toLocaleString()} kWh/mo`}
                    note="lesser of output & usage"
                  />
                  <CalcStep
                    n="4"
                    label="Monthly savings"
                    value={`₱${monthlySavings.toLocaleString("en-PH", { maximumFractionDigits: 0 })}`}
                    note={`offset × ₱${meralcoRate} × ${Math.round(offsetFactor * 100)}% bill offset`}
                  />
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg bg-section px-4 py-3 text-text-body">
                    <span className="font-semibold text-text-primary">Payback</span> = price ÷ yearly savings ={" "}
                    <span className="font-semibold">{paybackYears} yrs</span>
                  </div>
                  <div className="rounded-lg bg-section px-4 py-3 text-text-body">
                    <span className="font-semibold text-text-primary">25-yr net</span> = (yearly savings × 25) − price ={" "}
                    <span className="font-semibold">₱{netSavings25yr.toLocaleString()}</span>
                  </div>
                </div>

                <details className="mt-6">
                  <summary className="cursor-pointer text-sm font-semibold text-accent-sun hover:text-orange-600 select-none">
                    What this estimate assumes (and its limits) ▾
                  </summary>
                  <ul className="mt-3 space-y-2 text-sm text-text-body list-disc pl-5">
                    <li>
                      We size the system at roughly <span className="font-medium">1&nbsp;kW per ₱1,000</span>{" "}
                      of your monthly bill, snapped to the nearest available package — leaving headroom for
                      future usage (aircon, EV, etc.).
                    </li>
                    <li>
                      1&nbsp;kW of panels generates about {GEN_PER_KW}&nbsp;kWh/month — a typical Philippine
                      figure after system losses. Actual output varies with location, roof angle and shading.
                    </li>
                    <li>
                      We assume solar offsets about{" "}
                      <span className="font-medium">{Math.round(offsetFactor * 100)}%</span> of your bill
                      ({recommended.battery ? "hybrid — battery self-consumes more" : "grid-tie — daytime only"}).
                      The rest — fixed/transmission charges, taxes, and exported energy credited below the
                      ₱{meralcoRate}/kWh retail rate — isn't fully erased.
                    </li>
                    {recommended.battery && (
                      <li>
                        <span className="font-medium">Battery savings</span> uses the capacity method:{" "}
                        {recommended.batteryAh}Ah × {BATTERY_VOLTAGE}V = {batteryCapacityKwh.toFixed(2)}&nbsp;kWh,
                        {" "}× ₱{meralcoRate}/kWh = the maximum saved per full charge–discharge (≈once daily).
                        Real savings depend on how fully it cycles. Backup hours assume a ~1&nbsp;kW essential
                        load (fridge, lights, fans, Wi-Fi).
                      </li>
                    )}
                    <li>
                      Grid-tie offsets mainly daytime use; a <span className="font-medium">hybrid</span> stores energy
                      for night use and brownouts, improving self-consumption.
                    </li>
                    <li>
                      Figures exclude panel degradation (~0.5%/yr) and any future inverter/battery replacement.
                    </li>
                  </ul>
                  <p className="mt-3 text-xs text-text-muted">
                    Indicative estimate only. Actual savings depend on roof orientation, shading, weather, appliance
                    usage and Meralco rates. Request a free site assessment for an exact proposal.
                  </p>
                </details>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function CalcStep({ n, label, value, note }) {
  return (
    <div className="rounded-xl border border-border-light bg-section/60 p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="grid place-items-center h-6 w-6 rounded-full bg-accent-sun/15 text-accent-sun text-xs font-bold">
          {n}
        </span>
        <span className="text-xs font-semibold uppercase tracking-wide text-text-muted">{label}</span>
      </div>
      <div className="text-lg font-bold text-text-primary font-numeric">{value}</div>
      <div className="text-xs text-text-muted mt-0.5">{note}</div>
    </div>
  );
}

export default Main;
